import { useCallback } from "react";
import { ChangeEvent } from "react";

interface UseFormFieldHandlerOptions<T> {
  setFormState: React.Dispatch<React.SetStateAction<T>>;
  setTouchedFields: React.Dispatch<React.SetStateAction<Set<string>>>;
  numberFields?: string[];
  booleanFields?: string[];
  integerFields?: string[];
}

export const useFormFieldHandler = <T extends Record<string, any>>({
  setFormState,
  setTouchedFields,
  numberFields = [],
  booleanFields = [],
  integerFields = [],
}: UseFormFieldHandlerOptions<T>) => {
  const handleFieldChange = useCallback(
    (
      e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
      const { name, value, type } = e.target;

      // Handle checkbox inputs
      if (type === "checkbox") {
        const { checked } = e.target as HTMLInputElement;
        setFormState((prevState) => ({
          ...prevState,
          [name]: checked,
        }));
      } else {
        // Handle other input types
        let processedValue: any = value;

        // Convert to integer for specified fields (like gender, role, order)
        if (integerFields.includes(name)) {
          processedValue = parseInt(value, 10) || 0;
        }
        // Convert to number for specified fields
        else if (numberFields.includes(name)) {
          processedValue = parseFloat(value) || 0;
        }
        // Convert to boolean for specified fields
        else if (booleanFields.includes(name)) {
          processedValue = value === "true";
        }

        setFormState((prevState) => ({
          ...prevState,
          [name]: processedValue,
        }));
      }

      // Mark field as touched
      setTouchedFields((prev) => new Set(prev).add(name));
    },
    [setFormState, setTouchedFields, numberFields, booleanFields, integerFields]
  );

  const handleFieldBlur = useCallback(
    (fieldName: string) => {
      setTouchedFields((prev) => new Set(prev).add(fieldName));
    },
    [setTouchedFields]
  );

  const handleDirectFieldChange = useCallback(
    (fieldName: string, value: any) => {
      setFormState((prevState) => ({
        ...prevState,
        [fieldName]: value,
      }));

      // Mark field as touched
      setTouchedFields((prev) => new Set(prev).add(fieldName));
    },
    [setFormState, setTouchedFields]
  );

  return {
    handleFieldChange,
    handleFieldBlur,
    handleDirectFieldChange,
  };
};
